/**
 * src/components/analytics/ScrollDepthTracker.tsx
 *
 * GA4 scroll engagement tracker — sayfanın %25, %50, %75, %100'üne kullanıcı
 * scroll edince milestone event gönderir (her threshold için tek seferlik).
 *
 * Neden değerli? page_view event'i sadece yüklenme; scroll_depth gerçek
 * engagement göstergesi. "%50'ye kadar inenler" = içerik gerçekten okundu.
 *
 * Performance: rAF throttle (raw scroll event listener her frame değil, max
 * frame rate'te). passive: true scrolling block etmez.
 *
 * Consent: trackEvent no-op pattern.
 */
'use client'

import { useEffect } from 'react'

import { trackEvent } from '@/lib/analytics/gtag'

const THRESHOLDS = [25, 50, 75, 100] as const

export function ScrollDepthTracker() {
  useEffect(() => {
    if (typeof window === 'undefined') return

    const fired = new Set<number>()
    let rafId: number | null = null

    const check = () => {
      rafId = null
      const docEl = document.documentElement
      const scrollY = window.scrollY || docEl.scrollTop
      const winH = window.innerHeight
      const docH = Math.max(docEl.scrollHeight, document.body.scrollHeight)
      // Scrollable yükseklik = docH - winH (sayfa ekrandan büyükse)
      const scrollable = Math.max(docH - winH, 1)
      const pct = Math.min(100, Math.round(((scrollY + winH) / docH) * 100))
      // Daha hassas: scrolledPct (sayfanın ne kadarına inildi)
      // Burada (scrollY+winH)/docH = scrollable + viewport oranı
      // 100% = en altta
      void scrollable

      for (const threshold of THRESHOLDS) {
        if (pct >= threshold && !fired.has(threshold)) {
          fired.add(threshold)
          trackEvent('scroll_depth', { percent: threshold })
        }
      }
    }

    const onScroll = () => {
      if (rafId !== null) return
      rafId = requestAnimationFrame(check)
    }

    // İlk render'da kısa sayfalar zaten %100 görünür
    check()

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [])

  return null
}
